import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const EMPTY_CUSTOM = {
  label: '',
  fieldKey: '',
  fieldType: 'text',
  placeholder: '',
  helpText: '',
  required: false,
};

const ConfigFormBuilder = ({ pageKey, formId, onSelectForm }) => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(pageKey || '');
  const [form, setForm] = useState(null);
  const [classFilter, setClassFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [flags, setFlags] = useState({});
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customDraft, setCustomDraft] = useState(EMPTY_CUSTOM);
  const [addingCustom, setAddingCustom] = useState(false);
  const [permFieldId, setPermFieldId] = useState(null);
  const [permRows, setPermRows] = useState([]);
  const [, setPermRoles] = useState([]);
  const [permFieldMeta, setPermFieldMeta] = useState(null);
  const [permSaving, setPermSaving] = useState(false);

  const reloadForm = useCallback(async (id) => {
    if (!id) {
      setForm(null);
      return;
    }
    const res = await adminAPI.getConfigForm(id);
    setForm(res?.data?.data || res?.data);
  }, []);

  useEffect(() => {
    adminAPI.getConfigPages()
      .then((res) => setPages(res?.data?.data || res?.data || []))
      .catch(() => notifyError('Unable to load pages'));
    adminAPI.getConfigFlags()
      .then((res) => setFlags(res?.data?.flags || res?.data || {}))
      .catch(() => setFlags({}));
  }, []);

  useEffect(() => {
    if (pageKey) setSelectedPage(pageKey);
  }, [pageKey]);

  useEffect(() => {
    reloadForm(formId).catch(() => notifyError('Unable to load form'));
  }, [formId, reloadForm]);

  const loadPageForms = async (key) => {
    setSelectedPage(key);
    try {
      const res = await adminAPI.getConfigPage(key);
      const page = res?.data?.data || res?.data;
      const first = (page?.forms || [])[0];
      onSelectForm?.(first ? first.id : null);
    } catch (_e) {
      notifyError('Unable to load page forms');
    }
  };

  const startEdit = (f) => {
    setEditingId(f.id);
    setDraft({
      label: f.label || '',
      helpText: f.helpText || '',
      placeholder: f.placeholder || '',
      displayOrder: f.displayOrder ?? 0,
      hidden: Boolean(f.hidden),
      readOnly: Boolean(f.readOnly),
      required: Boolean(f.required),
    });
  };

  const saveField = async (f) => {
    setSaving(true);
    try {
      await adminAPI.updateConfigField(f.id, draft);
      notifySuccess('Field presentation saved (draft). Publish to apply on live forms.');
      setEditingId(null);
      await reloadForm(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to save field');
    } finally {
      setSaving(false);
    }
  };

  const moveField = async (f, direction) => {
    if (!f.allowAdminReorder || !form?.fields) return;
    const sorted = [...form.fields].sort((a, b) => a.displayOrder - b.displayOrder);
    const idx = sorted.findIndex((x) => x.id === f.id);
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapWith < 0 || swapWith >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapWith];
    try {
      await adminAPI.reorderConfigFields([
        { id: a.id, displayOrder: b.displayOrder },
        { id: b.id, displayOrder: a.displayOrder },
      ]);
      await reloadForm(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Reorder failed');
    }
  };

  const publish = async () => {
    if (!formId) return;
    setPublishing(true);
    try {
      await adminAPI.publishConfigForm(formId, { changeSummary: `Published form ${form?.formKey}` });
      notifySuccess('Form configuration published. Live pages will use overrides.');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const addCustomField = async () => {
    if (!formId || !customDraft.label.trim()) {
      notifyError('Label is required for a custom field.');
      return;
    }
    setAddingCustom(true);
    try {
      await adminAPI.createConfigCustomField(formId, customDraft);
      notifySuccess('Custom field added (draft). Publish to show on QA/QC.');
      setCustomDraft(EMPTY_CUSTOM);
      setShowAddCustom(false);
      await reloadForm(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to add custom field');
    } finally {
      setAddingCustom(false);
    }
  };

  const addSuggested = async () => {
    if (!formId) return;
    if (!(await confirmDialog('Add suggested QA/QC custom fields (lot, workfront, NCR, consultant, closure due)?', 'Suggested fields'))) return;
    setAddingCustom(true);
    try {
      const res = await adminAPI.addSuggestedCustomFields(formId);
      const n = (res?.data?.created || []).length;
      notifySuccess(n ? `Added ${n} suggested field(s). Publish to apply.` : 'Suggested fields already present.');
      await reloadForm(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to add suggested fields');
    } finally {
      setAddingCustom(false);
    }
  };


  const openPermissions = async (f) => {
    if (f.fieldClass !== 'CUSTOM') return;
    try {
      const res = await adminAPI.getConfigFieldPermissions(f.id);
      const data = res?.data?.data || res?.data || {};
      setPermFieldMeta(data.field || { id: f.id, fieldKey: f.fieldKey, label: f.label });
      setPermRoles(data.roles || []);
      const byRole = Object.fromEntries((data.permissions || []).map((p) => [String(p.roleCode).toUpperCase(), p]));
      const rows = (data.roles || []).map((r) => {
        const code = String(r.code).toUpperCase();
        const existing = byRole[code];
        return existing
          ? {
            roleCode: code,
            canView: Boolean(existing.canView),
            canCreate: Boolean(existing.canCreate),
            canEdit: Boolean(existing.canEdit),
            canClear: Boolean(existing.canClear),
            canExport: Boolean(existing.canExport),
          }
          : {
            roleCode: code,
            canView: true,
            canCreate: true,
            canEdit: true,
            canClear: false,
            canExport: true,
          };
      });
      setPermRows(rows);
      setPermFieldId(f.id);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to load permissions');
    }
  };

  const savePermissions = async () => {
    if (!permFieldId) return;
    setPermSaving(true);
    try {
      await adminAPI.saveConfigFieldPermissions(permFieldId, permRows);
      notifySuccess('Field permissions saved. Applied at runtime immediately for custom values.');
      setPermFieldId(null);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to save permissions');
    } finally {
      setPermSaving(false);
    }
  };

  const togglePerm = (roleCode, key) => {
    setPermRows((prev) => prev.map((r) => (
      r.roleCode === roleCode ? { ...r, [key]: !r[key] } : r
    )));
  };

  const archiveField = async (f) => {
    if (f.fieldClass !== 'CUSTOM') return;
    if (!(await confirmDialog(`Archive custom field "${f.label}"? Existing values are kept but the field is hidden.`, 'Archive field'))) return;
    try {
      await adminAPI.archiveConfigField(f.id);
      notifySuccess('Custom field archived. Publish to update live form.');
      await reloadForm(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to archive field');
    }
  };

  const fields = (form?.fields || []).filter((f) => classFilter === 'all' || f.fieldClass === classFilter)
    .filter((f) => f.status !== 'ARCHIVED')
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const notConfigurable = form?.configurationScope === 'NOT_CONFIGURABLE'
    || form?.page?.configurationScope === 'NOT_CONFIGURABLE';
  const publishingEnabled = flags.ENABLE_CONFIG_PUBLISHING === true;
  const customEnabled = flags.ENABLE_CUSTOM_FIELDS === true;
  const fieldPermsEnabled = flags.ENABLE_FIELD_LEVEL_PERMISSIONS === true;
  const CUSTOM_FIELD_PAGES = new Set([
    'qaqc',
    'correspondence_register',
    'thread_detail',
    'drawings_design',
    'milestones',
    'admin_projects',
    'bank_guarantees',
    'ra_bills',
  ]);
  const pageAllowsCustom = CUSTOM_FIELD_PAGES.has(form?.page?.pageKey || selectedPage);

  return (
    <div>
      <p className="ce-muted">
        Phase 2–7: presentation, CUSTOM fields (wired pages), and role field permissions on CUSTOM fields.
        Changes stay draft until you Publish{publishingEnabled ? '' : ' (ENABLE_CONFIG_PUBLISHING is off)'}.
      </p>
      <div className="filter-bar">
        <select value={selectedPage} onChange={(e) => loadPageForms(e.target.value)}>
          <option value="">Select page</option>
          {pages.map((p) => <option key={p.pageKey} value={p.pageKey}>{p.displayName}</option>)}
        </select>
        <FormPicker pageKey={selectedPage} formId={formId} onSelectForm={onSelectForm} />
        <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
          <option value="all">All classes</option>
          <option value="CORE_SYSTEM">CORE_SYSTEM</option>
          <option value="STANDARD_CONFIGURABLE">STANDARD_CONFIGURABLE</option>
          <option value="CUSTOM">CUSTOM</option>
          <option value="COMPUTED">COMPUTED</option>
        </select>
        {form && !notConfigurable && (
          <button type="button" className="btn-primary" disabled={publishing || !publishingEnabled} onClick={publish}>
            {publishing ? 'Publishing…' : 'Publish Form Config'}
          </button>
        )}
      </div>

      {form && !notConfigurable && customEnabled && pageAllowsCustom && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setShowAddCustom((v) => !v)}>
            {showAddCustom ? 'Cancel add' : 'Add custom field'}
          </button>
          <button type="button" className="btn-secondary btn-sm" disabled={addingCustom} onClick={addSuggested}>
            Add suggested QA/QC fields
          </button>
          {!customEnabled && <span className="ce-muted">ENABLE_CUSTOM_FIELDS is off</span>}
        </div>
      )}

      {showAddCustom && (
        <div className="ce-detail" style={{ marginBottom: 12 }}>
          <h4 style={{ marginTop: 0 }}>New custom field</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Label *</label>
              <input value={customDraft.label} onChange={(e) => setCustomDraft({ ...customDraft, label: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Key (optional)</label>
              <input
                value={customDraft.fieldKey}
                placeholder="auto from label"
                onChange={(e) => setCustomDraft({ ...customDraft, fieldKey: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select value={customDraft.fieldType} onChange={(e) => setCustomDraft({ ...customDraft, fieldType: e.target.value })}>
                <option value="text">text</option>
                <option value="textarea">textarea</option>
                <option value="number">number</option>
                <option value="date">date</option>
                <option value="boolean">boolean</option>
                <option value="email">email</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Placeholder</label>
            <input value={customDraft.placeholder} onChange={(e) => setCustomDraft({ ...customDraft, placeholder: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Help text</label>
            <textarea rows={2} value={customDraft.helpText} onChange={(e) => setCustomDraft({ ...customDraft, helpText: e.target.value })} />
          </div>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
            <input type="checkbox" checked={customDraft.required} onChange={(e) => setCustomDraft({ ...customDraft, required: e.target.checked })} />
            Required
          </label>
          <div>
            <button type="button" className="btn-primary btn-sm" disabled={addingCustom} onClick={addCustomField}>
              {addingCustom ? 'Saving…' : 'Create custom field'}
            </button>
          </div>
        </div>
      )}

      {!form ? (
        <div className="empty-state"><p>Select a page and form to configure presentation.</p></div>
      ) : (
        <div className="ce-detail">
          <h3 style={{ marginTop: 0 }}>{form.displayName} <span className="ce-badge">{form.formType}</span></h3>
          <p className="ce-muted">
            {form.formKey} · Scope {form.configurationScope}
            {notConfigurable ? ' · editing locked' : ''}
            {customEnabled && pageAllowsCustom ? ' · custom fields enabled' : ''}
          </p>
          {fields.map((f) => (
            <div key={f.id} className="ce-field-row">
              {editingId === f.id ? (
                <div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Label</label>
                      <input
                        value={draft.label}
                        disabled={!f.allowAdminRename}
                        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Display order</label>
                      <input
                        type="number"
                        value={draft.displayOrder}
                        disabled={!f.allowAdminReorder}
                        onChange={(e) => setDraft({ ...draft, displayOrder: Number(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Placeholder</label>
                    <input value={draft.placeholder} onChange={(e) => setDraft({ ...draft, placeholder: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Help text</label>
                    <textarea rows={2} value={draft.helpText} onChange={(e) => setDraft({ ...draft, helpText: e.target.value })} />
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
                    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={draft.hidden} disabled={!f.allowAdminHide} onChange={(e) => setDraft({ ...draft, hidden: e.target.checked })} />
                      Hidden
                    </label>
                    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={draft.readOnly} onChange={(e) => setDraft({ ...draft, readOnly: e.target.checked })} />
                      Read-only
                    </label>
                    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={draft.required} disabled={!f.allowAdminRequired} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} />
                      Required
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                    <button type="button" className="btn-primary btn-sm" disabled={saving || notConfigurable} onClick={() => saveField(f)}>Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <strong>{f.label}</strong>
                    <span>
                      <span className="ce-badge ce-badge--muted">{f.fieldType}</span>{' '}
                      <span className={`ce-badge ${f.fieldClass === 'CORE_SYSTEM' ? 'ce-badge--warn' : 'ce-badge--ok'}`}>{f.fieldClass}</span>
                      {f.status === 'DRAFT' ? <span className="ce-badge ce-badge--muted"> DRAFT</span> : null}
                    </span>
                  </div>
                  <div className="ce-muted">
                    key: {f.fieldKey} · order {f.displayOrder}
                    {f.required ? ' · required' : ''}
                    {f.readOnly ? ' · read-only' : ''}
                    {f.hidden ? ' · hidden' : ''}
                    {f.storageType === 'CUSTOM_VALUE' ? ' · CustomFieldValue' : ''}
                    {f.placeholder ? ` · placeholder: ${f.placeholder}` : ''}
                  </div>
                  {f.helpText && <div className="ce-muted">{f.helpText}</div>}
                  {!notConfigurable && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => startEdit(f)}>Edit presentation</button>
                      <button type="button" className="btn-secondary btn-sm" disabled={!f.allowAdminReorder} onClick={() => moveField(f, 'up')}>↑</button>
                      <button type="button" className="btn-secondary btn-sm" disabled={!f.allowAdminReorder} onClick={() => moveField(f, 'down')}>↓</button>
                      {f.fieldClass === 'CUSTOM' && customEnabled && fieldPermsEnabled && (
                        <button type="button" className="btn-secondary btn-sm" onClick={() => openPermissions(f)}>Permissions</button>
                      )}
                      {f.fieldClass === 'CUSTOM' && customEnabled && (
                        <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => archiveField(f)}>Archive</button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {permFieldId && (
        <div className="modal-overlay" style={{ zIndex: 80 }}>
          <div className="modal-content" style={{ maxWidth: 780 }}>
            <div className="modal-header">
              <h2>Field permissions — {permFieldMeta?.label || permFieldMeta?.fieldKey}</h2>
              <button type="button" className="btn-close" onClick={() => setPermFieldId(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="ce-muted">
                View / Create / Edit / Clear / Export by system role. Empty matrix (all defaults) means open access.
                If any role rows are saved, roles without a matching grant are denied view.
              </p>
              {!fieldPermsEnabled && (
                <p style={{ color: '#b45309' }}>ENABLE_FIELD_LEVEL_PERMISSIONS is off — save will be rejected until enabled.</p>
              )}
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>View</th>
                      <th>Create</th>
                      <th>Edit</th>
                      <th>Clear</th>
                      <th>Export</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permRows.map((r) => (
                      <tr key={r.roleCode}>
                        <td>{r.roleCode}</td>
                        {['canView', 'canCreate', 'canEdit', 'canClear', 'canExport'].map((k) => (
                          <td key={k}>
                            <input
                              type="checkbox"
                              checked={Boolean(r[k])}
                              onChange={() => togglePerm(r.roleCode, k)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!permRows.length && (
                      <tr><td colSpan={6}>No active system roles found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setPermFieldId(null)}>Cancel</button>
                <button type="button" className="btn-primary" disabled={permSaving || !fieldPermsEnabled} onClick={savePermissions}>
                  {permSaving ? 'Saving…' : 'Save permissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const FormPicker = ({ pageKey, formId, onSelectForm }) => {
  const [forms, setForms] = useState([]);
  useEffect(() => {
    if (!pageKey) { setForms([]); return; }
    adminAPI.getConfigPage(pageKey)
      .then((res) => setForms((res?.data?.data || res?.data)?.forms || []))
      .catch(() => setForms([]));
  }, [pageKey]);
  return (
    <select value={formId || ''} onChange={(e) => onSelectForm?.(e.target.value ? Number(e.target.value) : null)} disabled={!pageKey}>
      <option value="">Select form</option>
      {forms.map((f) => <option key={f.id} value={f.id}>{f.displayName} ({f.formType})</option>)}
    </select>
  );
};

export default ConfigFormBuilder;
